import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Menu, X, LogOut, UserCircle, ChevronDown, MessageSquare,
  ExternalLink, Facebook, Instagram, Linkedin, Twitter, Youtube,
  Globe, Mail, Phone, Rss, Github, MapPin, Calendar, Shield, ShieldCheck,
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import { useTranslation } from '../i18nContext';
import { buildLocalePath, isValidLocale } from '../i18n';
import { useAuth } from '../lib/AuthContext';
import { usePublicConfig } from '../lib/publicConfig';
import { api } from '../lib/api';
import logo from '../assets/logo.png';

// Maps backend `icon` keys → lucide-react components used in menu rendering.
const ICON_MAP = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  globe: Globe,
  mail: Mail,
  phone: Phone,
  'external-link': ExternalLink,
  rss: Rss,
  github: Github,
  'map-pin': MapPin,
  calendar: Calendar,
  // tiktok / whatsapp / telegram aren't in lucide; fall back to Globe.
  tiktok: Globe,
  whatsapp: Globe,
  telegram: Globe,
};

function IconFor({ name, size = 14 }) {
  if (!name) return null;
  const Cmp = ICON_MAP[name] || null;
  return Cmp ? <Cmp size={size} /> : null;
}

/**
 * Header — fully data-driven navigation rendered from /api/menus/tree.
 *
 * The tree is authored by admins at /admin/menus (drag-and-drop). Each node
 * has: label_es / label_en, path, path_kind ('route'|'bilingual'), visible,
 * is_dropdown, children. Two-level nesting is supported (root → dropdown
 * → leaf). Beyond that the tree is rendered flat as leaves under the parent.
 *
 * Special items (gallery, past_presidents_photos) are auto-hidden when their
 * backing data is disabled by the admin — admin/council users always see them.
 */
function resolveUrl(item, locale) {
  if (!item || item.is_dropdown) return null;
  const path = (item.path || '').trim();
  if (!path) return null;
  if (item.path_kind === 'external_url') {
    return path;  // absolute URL, rendered with <a target="_blank">.
  }
  if (item.path_kind === 'bilingual') {
    const [es = '', en = ''] = path.split('|');
    const slug = locale === 'en' ? (en || es) : (es || en);
    return slug ? `/${locale}/${slug}` : null;
  }
  // path_kind === 'route' → use buildLocalePath when the path matches a known route key
  return buildLocalePath(locale, path);
}

function isExternal(item) {
  return item && item.path_kind === 'external_url' && (item.path || '').trim();
}

/**
 * Single renderer for a clickable nav leaf — uses <a target="_blank"> for
 * external URLs (with the ExternalLink icon glyph) and react-router <Link>
 * otherwise. Accepts the testid + className from the caller.
 */
function MenuLink({ node, url, locale, className, testid, onClick, children }) {
  const labelText = locale === 'en' ? node.label_en : node.label_es;
  const iconEl = node.icon ? <IconFor name={node.icon} size={14} /> : null;
  if (isExternal(node)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className={className}
        onClick={onClick}
      >
        {iconEl}
        <span>{children ?? labelText}</span>
        <ExternalLink size={11} className="opacity-60" />
      </a>
    );
  }
  return (
    <Link to={url} data-testid={testid} className={className} onClick={onClick}>
      {iconEl}
      <span>{children ?? labelText}</span>
    </Link>
  );
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [tree, setTree] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);     // desktop hover/click
  const [mobileOpenIds, setMobileOpenIds] = useState(new Set());  // mobile expanded dropdowns
  const [userMenuOpen, setUserMenuOpen] = useState(false);        // desktop avatar menu
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const { pathname } = useLocation();
  const { lang } = useParams();
  const t = useTranslation();
  const locale = lang && isValidLocale(lang) ? lang : 'es';
  const { user, logout } = useAuth();
  const publicConfig = usePublicConfig();
  // During maintenance ("modo prueba"), standard members must not see the
  // navigation menus or any hint that an internal area exists. They can only
  // reach their own public profile (handled by Layout's routing gate).
  const hideInternal = !!(publicConfig && publicConfig.maintenance_mode) && !!user && user.role === 'interpreter';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu + desktop dropdowns on route change.
  useEffect(() => {
    setIsOpen(false);
    setOpenDropdownId(null);
    setUserMenuOpen(false);
    setMobileOpenIds(new Set());
  }, [pathname]);

  // Body scroll lock while mobile drawer is open.
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // Close desktop dropdown when clicking outside it.
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdownId(null);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch the nav tree once. Admin/council see the full tree (incl. hidden
  // items, server-side) so they can preview unpublished changes; public
  // visitors only get items where visible=true (filtered server-side).
  useEffect(() => {
    api.get('/menus/tree').then(({ data }) => setTree(data || [])).catch(() => setTree([]));
  }, []);

  // Auto-hide gallery menu items when their backing gallery is disabled by
  // the admin. This is STRICT — admins can preview disabled galleries via
  // /admin/photo-galleries → "Ver página pública". The header reflects the
  // public reality so admins don't get confused about what visitors see.
  const [galleryEnabled, setGalleryEnabled] = useState(true);
  const [ppPhotosLinkVisible, setPpPhotosLinkVisible] = useState(true);
  useEffect(() => {
    api.get('/photo-galleries').then(({ data }) => {
      const g = (data || []).find((r) => r.key === 'gallery');
      const pp = (data || []).find((r) => r.key === 'past-presidents-photos');
      setGalleryEnabled(!!(g && g.enabled));
      setPpPhotosLinkVisible(!!(pp && pp.enabled && (pp.display_mode || 'own_page') !== 'embedded'));
    }).catch(() => {});
  }, []);

  // Unread notifications badge for mobile "Comunidad" link.
  const [unreadTotal, setUnreadTotal] = useState(0);
  useEffect(() => {
    if (!user || (user.role !== 'interpreter' && user.role !== 'admin')) {
      setUnreadTotal(0); return;
    }
    let alive = true;
    const fetchUnread = () => {
      api.get('/me/notifications')
        .then(({ data }) => { if (alive) setUnreadTotal(data?.unread_count || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  // Filter out auto-hidden gallery items by menu_key. Strict: no staff bypass.
  function shouldHide(node) {
    if (node.menu_key === 'gallery' && !galleryEnabled) return true;
    if (node.menu_key === 'past_presidents_photos' && !ppPhotosLinkVisible) return true;
    return false;
  }

  // Recursively filter the tree: hide leaves by rule above + collapse empty dropdowns.
  function filterTreeRec(nodes) {
    return nodes
      .filter((n) => !shouldHide(n))
      .map((n) => ({ ...n, children: filterTreeRec(n.children || []) }))
      .filter((n) => !n.is_dropdown || n.children.length > 0);
  }
  const filteredTree = filterTreeRec(tree || []);
  const navTree = hideInternal ? [] : filteredTree;

  const isActive = (to) => to && (pathname === to || pathname.startsWith(to + '/'));

  function toggleMobileDropdown(id) {
    setMobileOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function renderDesktopNode(node) {
    const url = resolveUrl(node, locale);
    if (node.is_dropdown && node.children.length > 0) {
      return (
        <div key={node.id} className="relative" ref={openDropdownId === node.id ? dropdownRef : null}>
          <button
            onClick={() => setOpenDropdownId(openDropdownId === node.id ? null : node.id)}
            data-testid={`nav-${node.menu_key || node.id}`}
            className="px-2.5 py-2 text-sm font-medium rounded-md text-charcoal-700 hover:text-cmic-500 hover:bg-cmic-50 inline-flex items-center gap-1"
          >
            {locale === 'en' ? node.label_en : node.label_es}
            <ChevronDown size={14} className={`transition-transform ${openDropdownId === node.id ? 'rotate-180' : ''}`} />
          </button>
          {openDropdownId === node.id && (
            <div className="absolute left-0 top-full mt-1.5 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-50">
              {node.children.map((child) => {
                if (child.is_dropdown && (child.children || []).length > 0) {
                  return (
                    <div key={child.id} className="relative group">
                      <button
                        type="button"
                        data-testid={`nav-${child.menu_key || child.id}`}
                        className="w-full text-left flex items-center justify-between px-4 py-2.5 text-sm text-charcoal-700 hover:bg-cmic-50 hover:text-cmic-700"
                      >
                        <span>{locale === 'en' ? child.label_en : child.label_es}</span>
                        <ChevronDown size={14} className="-rotate-90" />
                      </button>
                      <div className="absolute left-full top-0 ml-0.5 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-50 hidden group-hover:block">
                        {(child.children || []).map((gc) => {
                          const gcUrl = resolveUrl(gc, locale);
                          return gcUrl ? (
                            <MenuLink
                              key={gc.id}
                              node={gc}
                              url={gcUrl}
                              locale={locale}
                              testid={`nav-${gc.menu_key || gc.id}`}
                              className="block px-4 py-2.5 text-sm text-charcoal-700 hover:bg-cmic-50 hover:text-cmic-700 inline-flex w-full items-center gap-1.5"
                            />
                          ) : null;
                        })}
                      </div>
                    </div>
                  );
                }
                const cu = resolveUrl(child, locale);
                return cu ? (
                  <MenuLink
                    key={child.id}
                    node={child}
                    url={cu}
                    locale={locale}
                    testid={`nav-${child.menu_key || child.id}`}
                    className="block px-4 py-2.5 text-sm text-charcoal-700 hover:bg-cmic-50 hover:text-cmic-700 inline-flex w-full items-center gap-1.5"
                  />
                ) : null;
              })}
            </div>
          )}
        </div>
      );
    }
    if (!url) return null;
    return (
      <MenuLink
        key={node.id}
        node={node}
        url={url}
        locale={locale}
        testid={`nav-${node.menu_key || node.id}`}
        className={`px-2.5 py-2 text-sm font-medium rounded-md transition-colors duration-200 inline-flex items-center gap-1.5 ${
          isActive(url) ? 'text-cmic-600 bg-cmic-50' : 'text-charcoal-700 hover:text-cmic-500 hover:bg-cmic-50'
        }`}
      />
    );
  }

  function renderMobileNode(node) {
    if (node.is_dropdown && node.children.length > 0) {
      const open = mobileOpenIds.has(node.id);
      return (
        <div key={node.id}>
          <button
            onClick={() => toggleMobileDropdown(node.id)}
            data-testid={`mobile-nav-${node.menu_key || node.id}-toggle`}
            aria-expanded={open}
            className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-charcoal-700 hover:text-cmic-500 hover:bg-cmic-50 rounded-lg"
          >
            <span>{locale === 'en' ? node.label_en : node.label_es}</span>
            <ChevronDown size={18} className={`transition-transform duration-200 ${open ? 'rotate-180 text-cmic-500' : 'text-charcoal-400'}`} />
          </button>
          {open && (
            <div className="ml-3 pl-3 border-l-2 border-cmic-100 space-y-0.5 pb-1">
              {node.children.map((child) => {
                if (child.is_dropdown && (child.children || []).length > 0) {
                  return (
                    <div key={child.id} className="space-y-0.5">
                      <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-cmic-600 mt-2">
                        {locale === 'en' ? child.label_en : child.label_es}
                      </div>
                      <div className="ml-3 pl-3 border-l-2 border-cmic-100 space-y-0.5">
                        {(child.children || []).map((gc) => {
                          const u = resolveUrl(gc, locale);
                          return u ? (
                            <MenuLink
                              key={gc.id}
                              node={gc}
                              url={u}
                              locale={locale}
                              onClick={() => setIsOpen(false)}
                              testid={`mobile-nav-${gc.menu_key || gc.id}`}
                              className="block px-4 py-2 text-sm text-charcoal-600 hover:text-cmic-500 hover:bg-cmic-50 rounded-lg inline-flex w-full items-center gap-1.5"
                            />
                          ) : null;
                        })}
                      </div>
                    </div>
                  );
                }
                const u = resolveUrl(child, locale);
                return u ? (
                  <MenuLink
                    key={child.id}
                    node={child}
                    url={u}
                    locale={locale}
                    onClick={() => setIsOpen(false)}
                    testid={`mobile-nav-${child.menu_key || child.id}`}
                    className="block px-4 py-2.5 text-sm font-medium text-charcoal-600 hover:text-cmic-500 hover:bg-cmic-50 rounded-lg inline-flex w-full items-center gap-1.5"
                  />
                ) : null;
              })}
            </div>
          )}
        </div>
      );
    }
    const u = resolveUrl(node, locale);
    if (!u) return null;
    return (
      <MenuLink
        key={node.id}
        node={node}
        url={u}
        locale={locale}
        onClick={() => setIsOpen(false)}
        testid={`mobile-nav-${node.menu_key || node.id}`}
        className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors inline-flex w-full items-center gap-1.5 ${
          isActive(u) ? 'text-cmic-600 bg-cmic-50' : 'text-charcoal-700 hover:text-cmic-500 hover:bg-cmic-50'
        }`}
      />
    );
  }

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-6">
          <Link to={buildLocalePath(locale, 'home')} className="flex-shrink-0" data-testid="header-logo-link">
            <img src={logo} alt="CMIC" className="h-12 w-auto" />
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5">
            {navTree.map(renderDesktopNode)}

            <div className="ml-1 flex items-center gap-1">
              {!hideInternal && user && (user.role === 'interpreter' || user.role === 'admin') && (
                <NotificationBell />
              )}
              {user ? (
                <UserMenu user={user} locale={locale} t={t} logout={logout}
                  open={userMenuOpen} setOpen={setUserMenuOpen} menuRef={userMenuRef} hideInternal={hideInternal} />
              ) : null}
              <LanguageSwitcher />
            </div>
          </nav>

          <div className="flex items-center gap-2 xl:hidden">
            {!hideInternal && user && (user.role === 'interpreter' || user.role === 'admin') && (
              <NotificationBell />
            )}
            <LanguageSwitcher />
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
              className="p-2 rounded-md text-charcoal-700 hover:text-cmic-500 hover:bg-cmic-50 transition-colors"
              aria-label="Menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 top-20 bg-black/40 backdrop-blur-sm lg:hidden z-40 animate-fadeIn"
            onClick={() => setIsOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <aside
            className="fixed top-20 right-0 bottom-0 w-[88vw] max-w-sm bg-white shadow-2xl lg:hidden z-50 flex flex-col"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
          >
            <nav className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-1">
              {navTree.map(renderMobileNode)}

              {/* Member/admin actions */}
              {(user && (user.role === 'interpreter' || user.role === 'admin' || user.role === 'council')) && (
                <>
                  <div className="my-3 border-t border-gray-100" />
                  {!hideInternal && (
                    <Link
                      to={`/${locale}/${locale === 'es' ? 'interno' : 'internal'}`}
                      onClick={() => setIsOpen(false)}
                      data-testid="mobile-nav-forum"
                      className="flex items-center gap-2 px-4 py-3 text-base font-medium text-cmic-700 hover:bg-cmic-50 rounded-lg"
                    >
                      <MessageSquare size={16} />
                      <span className="flex-1">{locale === 'es' ? 'Hub Interno' : 'Internal Hub'}</span>
                      {unreadTotal > 0 && (
                        <span data-testid="mobile-nav-forum-badge"
                          className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold ring-2 ring-amber-100">
                          {unreadTotal > 99 ? '99+' : unreadTotal}
                        </span>
                      )}
                    </Link>
                  )}
                  {user.role === 'interpreter' && (
                    <Link to={buildLocalePath(locale, 'profile')} onClick={() => setIsOpen(false)} data-testid="mobile-nav-my-profile"
                      className="flex items-center gap-2 px-4 py-3 text-base font-medium text-cmic-700 hover:bg-cmic-50 rounded-lg">
                      <UserCircle size={16} /> {t.portal.myProfile}
                    </Link>
                  )}
                  {user.role === 'admin' && (
                    <Link to="/admin" onClick={() => setIsOpen(false)} data-testid="mobile-nav-admin"
                      className="flex items-center gap-2 px-4 py-3 text-base font-medium text-cmic-700 hover:bg-cmic-50 rounded-lg">
                      Admin
                    </Link>
                  )}
                </>
              )}
            </nav>

            {user && (
              <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                <button
                  onClick={() => { setIsOpen(false); logout(); }}
                  data-testid="mobile-logout-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-base font-semibold text-charcoal-700 hover:bg-white border border-gray-200 rounded-lg"
                >
                  <LogOut size={16} /> {t.portal.logout}
                </button>
              </div>
            )}
          </aside>
        </>
      )}
    </header>
  );
}

const ROLE_LABELS = { admin: 'Administrador', council: 'Consejo', interpreter: 'Miembro' };

function UserMenu({ user, locale, t, logout, open, setOpen, menuRef, hideInternal }) {
  const name = user.full_name || user.username || (user.email || '').split('@')[0] || 'Usuario';
  const initials = name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'U';
  const isStaff = user.role === 'admin' || user.role === 'council';
  const profileSlug = locale === 'es' ? 'mi-perfil' : 'my-profile';

  const itemClass = 'flex items-center gap-2.5 px-4 py-2.5 text-sm text-charcoal-700 hover:bg-cmic-50 hover:text-cmic-700 transition-colors';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="user-menu-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-cmic-50 transition-colors"
        title={name}
      >
        <span className="w-9 h-9 rounded-full bg-gradient-to-br from-cmic-500 to-cmic-700 text-white flex items-center justify-center text-sm font-bold flex-shrink-0 overflow-hidden">
          {user.photo_url ? <img src={user.photo_url} alt="" className="w-full h-full object-cover" /> : initials}
        </span>
        <span className="hidden 2xl:block max-w-[120px] truncate text-sm font-medium text-charcoal-700">{name.split(' ')[0]}</span>
        <ChevronDown size={15} className={`text-charcoal-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-50" data-testid="user-menu-dropdown">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-charcoal-800 truncate">{name}</p>
            {user.email && <p className="text-xs text-charcoal-500 truncate">{user.email}</p>}
            <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wider text-cmic-700 bg-cmic-50 border border-cmic-100 rounded px-1.5 py-0.5">
              {ROLE_LABELS[user.role] || user.role}
            </span>
          </div>

          {(user.role === 'interpreter' || (isStaff && user.interpreter_slug)) && (
            <Link to={buildLocalePath(locale, 'profile')} data-testid="user-menu-profile" className={itemClass} onClick={() => setOpen(false)}>
              <UserCircle size={16} className="text-charcoal-400" /> {t.portal.myProfile}
            </Link>
          )}

          {!hideInternal && (
          <Link to={`/${locale}/${locale === 'es' ? 'interno' : 'internal'}`} data-testid="user-menu-forum" className={itemClass} onClick={() => setOpen(false)}>
            <MessageSquare size={16} className="text-charcoal-400" /> {locale === 'es' ? 'Área interna' : 'Internal area'}
          </Link>
          )}

          <Link to={`/${locale}/${profileSlug}/${locale === 'es' ? 'seguridad' : 'security'}`} data-testid="user-menu-security" className={itemClass} onClick={() => setOpen(false)}>
            <ShieldCheck size={16} className="text-charcoal-400" /> {locale === 'es' ? 'Seguridad' : 'Security'}
          </Link>

          {isStaff && (
            <Link to="/admin" data-testid="user-menu-admin" className={itemClass} onClick={() => setOpen(false)}>
              <Shield size={16} className="text-charcoal-400" /> {locale === 'es' ? 'Panel de administración' : 'Admin panel'}
            </Link>
          )}

          <div className="my-1 border-t border-gray-100" />
          <button onClick={() => { setOpen(false); logout(); }} data-testid="user-menu-logout"
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors">
            <LogOut size={16} /> {t.portal.logout}
          </button>
        </div>
      )}
    </div>
  );
}
